/**
 * The one button.
 *
 * Four variants and two sizes, and nothing else. The screens had grown their
 * own grammar — Save was a plain button on two screens and `primary` on two
 * others, Delete was a full button here and a `.link` there, and one screen
 * painted an orange primary Save on every table row. Orange marks the ONE thing
 * on a page you act on; a closed set of variants is what stops that rule being
 * re-decided per screen.
 *
 *   primary    the page's main action — at most one visible at a time
 *   secondary  everything else that does something
 *   ghost      chrome: icon buttons, dismiss, toggles
 *   danger     destructive, and asks for the same weight as primary so it is
 *              never the easiest button to hit by accident
 */

import type React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon, sized by the button. */
  icon?: React.ReactNode;
  /** Icon-only: the label becomes the accessible name and is not drawn. */
  iconOnly?: boolean;
  /** `submit` only inside a form that means it. */
  type?: 'button' | 'submit';
  busy?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconOnly = false,
  type = 'button',
  busy = false,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps): React.JSX.Element {
  const classes = ['btn', `btn-${variant}`, `btn-${size}`];
  if (iconOnly) classes.push('btn-icon');
  if (className !== undefined) classes.push(className);
  const label = iconOnly && typeof children === 'string' ? children : undefined;
  return (
    <button
      type={type}
      className={classes.join(' ')}
      disabled={disabled === true || busy}
      aria-busy={busy || undefined}
      {...(label !== undefined ? { 'aria-label': label, title: label } : {})}
      {...rest}
    >
      {icon !== undefined && <span className="btn-icon-slot">{icon}</span>}
      {!iconOnly && <span>{children}</span>}
    </button>
  );
}

/**
 * A download, styled as a secondary button.
 *
 * An anchor rather than a button with a click handler, because the browser's
 * own download is the right behaviour: the session cookie goes with it, the
 * file lands in Downloads under the name the server gave it, and nothing is
 * buffered in memory first.
 */
export function DownloadLink({
  href,
  icon,
  children,
}: {
  href: string;
  icon?: React.ReactNode;
  children: string;
}): React.JSX.Element {
  return (
    <a className="btn btn-secondary btn-md" href={href} download>
      {icon !== undefined && <span className="btn-icon-slot">{icon}</span>}
      <span>{children}</span>
    </a>
  );
}
